/**
 * menu.ts — The level picker.
 *
 * A DOM overlay, like the HUD and for the same reasons: crisp at any
 * resolution, costs no draw calls, and gets working buttons and focus handling
 * for free rather than reinventing them in WebGL.
 *
 * ## What it does and doesn't decide
 *
 * It renders the campaign and reports which level was clicked. It does not
 * build levels, start games, or know what a `LevelSpec` is — `main.ts` owns
 * that. Keeping it that way is what makes the whole thing testable under jsdom
 * with no renderer at all.
 *
 * ## What is unlocked
 *
 * One level ahead of where you are: everything up to and including your first
 * unfinished level, plus anything already finished. So a new player sees level 1
 * lit and the rest greyed, and each win lights the next one.
 *
 * The rule is written as "up to the **frontier**" rather than "the level after
 * the highest one beaten", and the difference is what happens to a save with a
 * hole in it — a cloud sync from a device that had beaten 40 levels, or progress
 * from before levels locked at all. Those levels stay playable (they were
 * earned) without the hole quietly unlocking the rest of the campaign.
 *
 * A locked button is `disabled` and says so to a screen reader, rather than
 * being hidden — the numbers still run to 1000, so the campaign's length is
 * visible even though its contents are not.
 *
 * Level 1 is the **guided walk** and says so: it carries the word "Tutorial"
 * where every other button carries its band, and its own blue swatch rather
 * than a place on the difficulty scale. It is otherwise an ordinary campaign
 * level — playable, replayable, and ticked like any other.
 *
 * **A locked level shows no band**, in the label or in its colour. Only the
 * levels that can be played say what they are. Two reasons: a grid where
 * every button carries a word is a wall of text to read past to find the one
 * you can press, and what the walk after next turns out to be is worth finding
 * out by getting there. The band arrives with the unlock.
 *
 * `?level=N` in the URL still goes anywhere — it is the deliberate escape hatch
 * for sharing a town and for development, and it counts toward progress exactly
 * as the menu does.
 *
 * ## One list, scrolled
 *
 * The campaign is a thousand levels long, and the screen it deserves is a list
 * you scroll — not a nav row, a jump menu and nineteen pages to click through.
 * A page count is chrome the player has to operate; a scrollbar is chrome they
 * already know.
 *
 * Two things make that work at this length:
 *
 * **It opens where the player is.** The list scrolls to the frontier — the level
 * they are up to — so the thing they came here to press is the first thing under
 * their eyes, with everything they have finished a scroll above it. That is the
 * job pagination was badly doing.
 *
 * **It grows as it is scrolled.** Only `CHUNK` levels past the frontier are
 * built up front; reaching the bottom appends another `CHUNK`. So a new player's
 * menu is sixty buttons rather than a thousand, and the ramp ahead is still there
 * for anyone who wants to scroll and look at it. Growth is append-only, which is
 * what keeps the scroll position steady — nothing above the viewport ever moves.
 *
 * The header and the foot sit **outside** the scrolling list rather than above
 * and below it in one long column. A menu whose title scrolls away is a menu
 * that can hide its own reset button, and on a phone the old centred-overlay
 * version clipped the title off the top of the scroll entirely.
 *
 * ## The account row
 *
 * Signing in lives here because this is the one screen that is always reachable
 * and never in the way of a run. The menu takes a `CloudSaveHandle` — a handful
 * of getters and three methods — and knows nothing about Firebase, in the same
 * way it knows nothing about `LevelSpec`. Hand it nothing and the row simply
 * isn't drawn, which is what a build with no Firebase config gets.
 */
import { CAMPAIGN_LENGTH, bandLabel, campaignSlice } from '../systems/levelgen/campaign';
import { clearProgress, completedLevels } from './progress';
import { CloudEvents, type CloudSaveHandle } from '../cloud/session';
import type { Difficulty } from '../systems/levelgen/types';

/** One swatch per band, so the ramp is visible before you play any of it. */
const BAND_COLOR: Record<Difficulty, string> = {
  easy: '#7ee0a0',
  medium: '#ffe08a',
  hard: '#ffa96b',
  'very-hard': '#ff8a8a',
};

/**
 * The guided walk's own swatch — off the band scale entirely, in the same blue
 * the tutorial card is trimmed with. Level 1 is not "the easiest level", it is
 * the one that teaches the game, and a player scanning the grid should be able
 * to see that before they read a word of it.
 */
const TUTORIAL_COLOR = '#8ecbff';

/**
 * How many levels are added at a time: built past the frontier when the menu
 * opens, and appended again each time the list is scrolled to the bottom.
 *
 * Sixty is twelve rows of five — comfortably more than a tall desktop window
 * shows, so there is always something below the fold to scroll toward and the
 * list never runs out mid-gesture.
 */
export const CHUNK = 60;

/** How close to the bottom, in px, counts as "scrolled to the end". */
const LOAD_MARGIN = 240;

/** Breathing room left above the frontier when the list scrolls to it. */
const SCROLL_GAP = 12;

const CSS = `
.menu{position:fixed;inset:0;z-index:20;display:none;flex-direction:column;
  align-items:center;justify-content:center;
  background:radial-gradient(circle at 50% 32%,#2c3540 0%,#1d232a 70%);
  font-family:system-ui,Arial,sans-serif;color:#fff}
.menu.is-open{display:flex}
/* A column that never outgrows the window: the header and the foot keep their
   height and the list in between takes what's left. The min-height:0 on the
   list is what actually lets it shrink — without it a flex item refuses to go
   below its content height and the panel overflows the screen instead.
   (No backticks in here: this block is a template literal.) */
.menu-panel{display:flex;flex-direction:column;box-sizing:border-box;
  padding:24px 20px;text-align:center;max-width:560px;width:100%;max-height:100%}
.menu h1{margin:0 0 4px;font-size:40px;letter-spacing:.5px}
.menu-sub{margin:0 0 16px;font-size:15px;opacity:.72}
.menu-list{position:relative;flex:1 1 auto;min-height:0;overflow-y:auto;
  /* the scrollbar sits beside the last column rather than over it */
  padding-right:6px}
.menu-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
/* The min-height is load-bearing: a locked button has no band under its number,
   and without a floor the rows with one would stand taller than the rows
   without, which reads as a broken grid rather than a deliberate difference. */
.menu-level{position:relative;padding:10px 4px;min-height:56px;box-sizing:border-box;
  border:0;border-radius:12px;
  background:rgba(255,255,255,.07);color:#fff;cursor:pointer;font:inherit;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;
  border-top:3px solid transparent;transition:background .12s,transform .12s}
.menu-level:hover:not(:disabled),.menu-level:focus-visible{background:rgba(255,255,255,.15);
  transform:translateY(-2px);outline:none}
.menu-level b{font-size:22px;font-weight:700;line-height:1}
.menu-level span{font-size:10px;opacity:.75;letter-spacing:.3px}
.menu-level i{position:absolute;top:3px;right:6px;font-style:normal;font-size:11px;
  color:#7ee0a0}
.menu-level.is-done{background:rgba(126,224,160,.14)}
/* Locked: dimmed rather than hidden, and still carrying its band — a level you
   can't see yet is a level you can't look forward to. */
.menu-level.is-locked{background:rgba(255,255,255,.03);color:#8b95a1;opacity:.55;
  cursor:default}
.menu-level.is-locked i{color:#8b95a1;font-size:10px}
.menu-foot{margin-top:16px;font-size:12px;opacity:.6;display:flex;
  align-items:center;justify-content:center;gap:14px;flex-wrap:wrap;flex:0 0 auto}
.menu-reset{border:0;background:none;color:inherit;font:inherit;cursor:pointer;
  text-decoration:underline;opacity:.8;padding:0}
.menu-reset:hover{opacity:1}
.menu-account{margin-top:16px;display:flex;flex-direction:column;align-items:center;gap:7px;
  min-height:1px;flex:0 0 auto}
.menu-account:empty{display:none}
/* Google asks that its button keep a white (or solid dark) face, its own mark,
   and the wording "Sign in with Google" — so this one deliberately doesn't
   inherit the translucent styling of everything else on this screen. */
.menu-signin{display:inline-flex;align-items:center;gap:10px;padding:9px 16px;border:0;
  border-radius:8px;background:#fff;color:#1f1f1f;cursor:pointer;
  font:500 14px/1 system-ui,Arial,sans-serif;box-shadow:0 1px 3px rgba(0,0,0,.3);
  transition:box-shadow .12s,transform .12s}
.menu-signin:hover:not(:disabled){box-shadow:0 2px 8px rgba(0,0,0,.4);transform:translateY(-1px)}
.menu-signin:disabled{opacity:.6;cursor:default}
.menu-signin svg{display:block;flex:0 0 auto}
.menu-who{font-size:12px;opacity:.72;display:flex;align-items:center;justify-content:center;
  gap:10px;flex-wrap:wrap}
.menu-signout{border:0;background:none;color:inherit;font:inherit;cursor:pointer;
  text-decoration:underline;opacity:.85;padding:0}
.menu-signout:hover{opacity:1}
.menu-sync{font-size:11px;opacity:.55}
.menu-sync.is-offline{color:#ffc48a;opacity:.85}
@media (max-width:520px){
  .menu h1{font-size:30px}
  .menu-grid{grid-template-columns:repeat(4,1fr)}
  .menu-level b{font-size:19px}}
`;

/**
 * Google's mark, inline. The brand guidelines want the real logo on the button,
 * and this game ships no image files — so it's a path, like everything else the
 * project draws.
 */
const GOOGLE_MARK = `
<svg viewBox="0 0 48 48" width="17" height="17" aria-hidden="true" focusable="false">
  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
</svg>`;

/** What the status line says under a signed-in player's name. */
const SYNC_NOTE: Record<string, string> = {
  syncing: 'Syncing…',
  synced: 'Progress saved to your account',
  offline: 'Offline — saved on this device',
};

/**
 * The account row is built with `innerHTML`, and the one value in it comes from
 * outside — a Google display name, which a player chooses themselves. Nothing
 * dangerous is reachable from a `<div>` in a menu, but interpolating a stranger's
 * string into markup is the habit worth not having.
 */
function escapeHtml(text: string): string {
  return text.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

/**
 * The **frontier**: the lowest level not yet finished, or `CAMPAIGN_LENGTH` once
 * they all are.
 *
 * Where the player is *up to*, not how many they have done — someone who skipped
 * level 3 and beat 4 through 40 is still up to level 3. It decides two things:
 * the page the menu opens on, and the last level that is unlocked.
 */
function firstUnfinished(done: ReadonlySet<number>): number {
  for (let n = 1; n <= CAMPAIGN_LENGTH; n++) {
    if (!done.has(n)) return n;
  }
  return CAMPAIGN_LENGTH;
}

export interface MenuOptions {
  /** Cloud save, if this build has any. Omitted, the account row isn't drawn. */
  cloud?: CloudSaveHandle;
}

export class Menu {
  private root: HTMLDivElement;
  private list: HTMLDivElement;
  private grid: HTMLDivElement;
  private account: HTMLDivElement;
  private reset: HTMLButtonElement;
  private tally: HTMLSpanElement;
  private styleEl: HTMLStyleElement;
  private teardown: Array<() => void> = [];
  private cloud?: CloudSaveHandle;
  /** The highest level currently built. Grows as the list is scrolled. */
  private shown = 0;

  /** Fired with the 1-based level number when one is clicked. */
  onPick?: (level: number) => void;

  constructor(parent: HTMLElement, options: MenuOptions = {}) {
    this.cloud = options.cloud;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    this.styleEl = style;

    this.root = document.createElement('div');
    this.root.className = 'menu';
    this.root.innerHTML = `
      <div class="menu-panel">
        <h1>🐕 Dog Walker</h1>
        <p class="menu-sub">Pick a walk — level 1 talks you through it. Every town is
          generated, and every one can be finished.</p>
        <div class="menu-list">
          <div class="menu-grid"></div>
        </div>
        <div class="menu-foot">
          <span class="menu-tally"></span>
          <button class="menu-reset" type="button">Reset progress</button>
        </div>
        <div class="menu-account"></div>
      </div>
    `;
    parent.appendChild(this.root);

    this.list = this.root.querySelector('.menu-list') as HTMLDivElement;
    this.grid = this.root.querySelector('.menu-grid') as HTMLDivElement;
    this.account = this.root.querySelector('.menu-account') as HTMLDivElement;
    this.reset = this.root.querySelector('.menu-reset') as HTMLButtonElement;
    this.tally = this.root.querySelector('.menu-tally') as HTMLSpanElement;

    this.buildGrid();
    this.buildAccount();

    // One listener on the scroll container, attached once — the grid inside it
    // is replaced wholesale, but the container never is.
    const onScroll = (): void => this.growIfAtEnd();
    this.list.addEventListener('scroll', onScroll);
    this.teardown.push(() => this.list.removeEventListener('scroll', onScroll));

    const onReset = (): void => {
      // Through cloud save when there's an account, or the ticks come straight
      // back on the next sync — see CloudSave.resetProgress.
      if (this.cloud) this.cloud.resetProgress();
      else clearProgress();
      this.buildGrid(); // rebuild rather than hunt for the ticks to remove
    };
    this.reset.addEventListener('click', onReset);
    this.teardown.push(() => this.reset.removeEventListener('click', onReset));

    if (this.cloud) {
      // One delegated listener rather than fresh ones on every rebuild: the row
      // is replaced whenever the status changes, and per-element handlers would
      // leak onto detached nodes exactly the way buildGrid's used to.
      const onClick = (event: Event): void => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('.menu-signin')) this.cloud?.signIn();
        else if (target?.closest('.menu-signout')) this.cloud?.signOut();
      };
      this.account.addEventListener('click', onClick);
      this.teardown.push(() => this.account.removeEventListener('click', onClick));

      // Progress can arrive from the account long after the menu was built.
      const onCloudChange = (): void => {
        this.buildAccount();
        this.buildGrid();
      };
      this.cloud.events.on(CloudEvents.CHANGED, onCloudChange);
      this.teardown.push(() => this.cloud?.events.off(CloudEvents.CHANGED, onCloudChange));
    }
  }

  /**
   * Draw the account row for the current status.
   *
   * Rebuilt wholesale like the grid, and for the same reason: six states with
   * different controls in them is exactly where patching in place starts
   * leaving a stale button behind.
   */
  private buildAccount(): void {
    const status = this.cloud?.status ?? 'unavailable';
    if (!this.cloud || status === 'unavailable') {
      this.account.replaceChildren();
      return;
    }

    // "Reset progress" is a much bigger promise once it reaches an account.
    this.reset.textContent = this.cloud.user ? 'Reset progress everywhere' : 'Reset progress';

    if (status === 'signed-out' || status === 'signing-in') {
      const signingIn = status === 'signing-in';
      this.account.innerHTML = `
        <button class="menu-signin" type="button"${signingIn ? ' disabled' : ''}>
          ${GOOGLE_MARK}<span>${signingIn ? 'Signing in…' : 'Sign in with Google'}</span>
        </button>
        <span class="menu-sync">Keep your progress across devices</span>
      `;
      return;
    }

    const label = this.cloud.user?.label || 'your account';
    this.account.innerHTML = `
      <span class="menu-who">
        <span>Signed in as ${escapeHtml(label)}</span>
        <button class="menu-signout" type="button">Sign out</button>
      </span>
      <span class="menu-sync${status === 'offline' ? ' is-offline' : ''}">${SYNC_NOTE[status]}</span>
    `;
  }

  /**
   * Rebuild the list from level 1 down to the frontier plus one chunk.
   *
   * Rebuilt from scratch whenever progress changes rather than patched in
   * place: a rebuild can't leave a stale tick behind the way a partial update
   * can, and it re-reads the frontier, which is what moves every time a walk is
   * won. How far it reaches is reset at the same time, so a menu reopened after
   * a long scroll is a short list again.
   */
  private buildGrid(): void {
    // Drop the previous buttons' listeners before their elements go, or every
    // reset leaks a handler onto a detached node.
    this.grid.replaceChildren();

    const done = completedLevels();
    this.tally.textContent = `${done.size} of ${CAMPAIGN_LENGTH} walks finished`;

    this.shown = 0;
    this.extendTo(Math.min(CAMPAIGN_LENGTH, firstUnfinished(done) + CHUNK), done);
  }

  /**
   * Append buttons up to level `last`.
   *
   * Append-only, which is the property that makes the growing list feel like a
   * long one rather than a reloading one: nothing already on screen moves, so
   * the scroll position stays exactly where the player's thumb left it.
   */
  private extendTo(last: number, done: ReadonlySet<number> = completedLevels()): void {
    // Read once for the whole batch rather than per button: it is a scan of the
    // campaign, and it cannot change while a chunk is being drawn.
    const frontier = firstUnfinished(done);

    for (const entry of campaignSlice(this.shown + 1, last - this.shown)) {
      const finished = done.has(entry.number);
      const locked = !finished && entry.number > frontier;

      const button = document.createElement('button');
      button.type = 'button';
      button.className =
        'menu-level' + (finished ? ' is-done' : '') + (locked ? ' is-locked' : '');
      button.dataset.level = String(entry.number);
      button.disabled = locked;
      const band = bandLabel(entry);
      // A locked level shows neither its band nor its colour — see the note on
      // the class about why what's coming stays unsaid.
      button.style.borderTopColor = locked
        ? '#4a535e'
        : entry.tutorial
          ? TUTORIAL_COLOR
          : BAND_COLOR[entry.difficulty];
      button.setAttribute(
        'aria-label',
        `Level ${entry.number}` +
          (locked ? ', locked — finish the level before it first' : `, ${band}`) +
          (finished ? ', completed' : ''),
      );
      button.innerHTML =
        `<b>${entry.number}</b>` +
        (locked ? '' : `<span>${band}</span>`) +
        (finished ? '<i>✓</i>' : '') +
        (locked ? '<i>🔒</i>' : '');

      button.addEventListener('click', () => this.onPick?.(entry.number));
      this.grid.appendChild(button);
    }
    // `max`, not assignment: a `last` at or below what is already drawn adds
    // nothing above (the slice is empty), and must not wind the mark backwards.
    this.shown = Math.max(this.shown, last);
  }

  /** Add another chunk once the list is scrolled near its end. */
  private growIfAtEnd(): void {
    if (this.shown >= CAMPAIGN_LENGTH) return;
    const remaining = this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight;
    if (remaining > LOAD_MARGIN) return;
    this.extendTo(Math.min(CAMPAIGN_LENGTH, this.shown + CHUNK));
  }

  /**
   * Put the frontier — the level the player is up to — at the top of the list.
   *
   * `offsetTop` is measured against `.menu-list`, which is the nearest
   * positioned ancestor, so it is already the scroll offset that brings the
   * button to the top. Levels below the frontier stay a scroll away rather than
   * out of reach, which is what makes replaying one still possible.
   */
  private scrollToFrontier(): void {
    const level = firstUnfinished(completedLevels());
    // Always present: `buildGrid` draws from level 1 to a chunk *past* the
    // frontier, so the button being looked for was made moments ago.
    const target = this.grid.querySelector(`.menu-level[data-level="${level}"]`) as HTMLElement;
    this.list.scrollTop = Math.max(0, target.offsetTop - SCROLL_GAP);
  }

  /**
   * Show the menu, picking up any progress made since it was last open — and
   * always on the level the player is up to, however far they scrolled last
   * time. That level is why they opened this screen.
   */
  open(): void {
    this.buildGrid();
    this.buildAccount();
    this.root.classList.add('is-open');
    // After `is-open`, because a `display:none` element has no layout and every
    // offset inside it would read as zero.
    this.scrollToFrontier();
  }

  close(): void {
    this.root.classList.remove('is-open');
  }

  get isOpen(): boolean {
    return this.root.classList.contains('is-open');
  }

  dispose(): void {
    for (const off of this.teardown) off();
    this.teardown = [];
    this.root.remove();
    this.styleEl.remove();
  }
}
